import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createElementVNode as _createElementVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, normalizeStyle as _normalizeStyle } from "vue"
import _imports_0 from '@/assets/icons/modal-close.svg'


import { ref, onBeforeMount, onBeforeUnmount, watch, useSlots, computed } from "vue";
import type { PropType } from "vue";
import { ModalStyleOptions } from "@/models/modal";

const mobileBreakpoint = 576;

const tabletBreakpoint = 992;


export default /*@__PURE__*/_defineComponent({
  __name: 'Modal',
  props: {
  closeBtn: {
    type: Boolean,
    default: true,
  },
  styleOptions: {
    type: Object as PropType<ModalStyleOptions>,
    required: false,
  },
  footerStyle: {
    type: String,
    default: "right",
    required: false,
  },
  closeBtnHidden: {
    type: Boolean,
    default: false,
  },
  allowClickOutsideToClose: {
    type: Boolean,
    default: false,
    required: false,
  },
},
  emits: ["close"],
  setup(__props, { expose: __expose, emit: __emit }) {

const emit = __emit;

const props = __props;

// Template Refs
const modalMask = ref<HTMLDivElement | null>(null);
const modalWrapper = ref<HTMLDivElement | null>(null);
const modalContainer = ref<HTMLDivElement | null>(null);
const modalHeader = ref<HTMLDivElement | null>(null);
const modalTitle = ref<HTMLDivElement | null>(null);
const modalBody = ref<HTMLDivElement | null>(null);
const modalFooter = ref<HTMLDivElement | null>(null);

const slots = useSlots();

const hasFooter = computed<boolean>(() => {
  return !!slots.footer;
});

const modalStyle = computed(() => {
  return {
    "--modal-container-maxWidth": props.styleOptions?.modalContainer?.maxWidth || "800px",
    "--modal-body-minHeight": props.styleOptions?.modalBody?.minHeight || "300px",
  };
});

const windowWidth = computed<number>(() => window.innerWidth);

const isMobile = computed<boolean>(() => windowWidth.value <= mobileBreakpoint);

const isTablet = computed<boolean>(() => windowWidth.value <= tabletBreakpoint);

const isDesktop = computed<boolean>(() => !isMobile.value && !isTablet.value);

const useMobileSizing = computed<boolean>(() => windowWidth.value <= 480);

onBeforeMount(() => {
  // When displaying a modal we tell the body that we don't
  // want the background to scroll
  const body = document.querySelector("body");
  if (body) {
    body.style.overflow = "hidden";
  }

  initializeStyle();
});

onBeforeUnmount(() => {
  // Right before we exit the modal we tell the body to perform as it did before
  const body = document.querySelector("body");
  if (body) {
    body.style.overflow = "visible";
  }
});

function disableScroll() {
  const body = document.querySelector("body");
  if (body) {
    body.style.overflow = "hidden";
  }
}

function enableScroll() {
  const body = document.querySelector("body");
  if (body) {
    body.style.overflow = "visible";
  }
}

function initializeStyle() {
  if (props.styleOptions) {
    if (props.styleOptions?.modalContainer && modalContainer.value) {
      modalContainer.value.style.maxWidth = props.styleOptions?.modalContainer?.maxWidth || "800px";
    }

    if (props.styleOptions?.modalTitle && modalTitle.value) {
      props.styleOptions?.modalTitle?.classes?.forEach((c: string) => {
        modalTitle.value?.classList.add(c);
      });
    }

    if (props.styleOptions?.modalBody && modalBody.value) {
      modalBody.value.style.minHeight = props.styleOptions?.modalBody?.minHeight || "300px";
    }

    if (props.styleOptions?.modalTitle && modalFooter.value) {
      props.styleOptions?.modalFooter?.classes?.forEach((c: string) => {
        modalFooter.value?.classList.add(c);
      });
    }
  }
}

function handleClickOutside(): void {
  if (props.allowClickOutsideToClose) {
    emit("close");
  }
}

watch(
  () => props.styleOptions,
  () => {
    initializeStyle();
  },
  {
    deep: true,
  },
);
__expose({
  modalMask,
  modalWrapper,
  modalContainer,
  modalHeader,
  modalBody,
  modalTitle,
  modalFooter,
  windowWidth,
  useMobileSizing,
  isMobile,
  isDesktop,
  isTablet,
  disableScroll,
  enableScroll,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "modal-mask",
    ref_key: "modalMask",
    ref: modalMask,
    style: _normalizeStyle(modalStyle.value)
  }, [
    _createElementVNode("div", {
      class: "modal-wrapper",
      ref_key: "modalWrapper",
      ref: modalWrapper,
      onClick: _withModifiers(handleClickOutside, ["self"])
    }, [
      _createElementVNode("div", {
        class: "modal-container",
        ref_key: "modalContainer",
        ref: modalContainer
      }, [
        (_ctx.$slots.header || __props.closeBtn)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "modal-header",
              ref_key: "modalHeader",
              ref: modalHeader
            }, [
              _createElementVNode("div", {
                class: "modal-title",
                ref_key: "modalTitle",
                ref: modalTitle
              }, [
                _renderSlot(_ctx.$slots, "header")
              ], 512),
              (__props.closeBtn)
                ? (_openBlock(), _createElementBlock("a", {
                    key: 0,
                    href: "",
                    class: _normalizeClass(["close-btn", { closeBtnHidden: __props.closeBtnHidden }]),
                    "aria-label": "Close",
                    onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (_ctx.$emit('close')), ["prevent"]))
                  }, [...(_cache[1] || (_cache[1] = [
                    _createElementVNode("img", {
                      src: _imports_0,
                      alt: "Close modal button"
                    }, null, -1)
                  ]))], 2))
                : _createCommentVNode("", true)
            ], 512))
          : _createCommentVNode("", true),
        _createElementVNode("div", _mergeProps({
          class: "modal-body",
          ref_key: "modalBody",
          ref: modalBody
        }, _ctx.$attrs), [
          _renderSlot(_ctx.$slots, "default")
        ], 16),
        (hasFooter.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 1,
              class: _normalizeClass(["modal-footer", __props.footerStyle]),
              ref_key: "modalFooter",
              ref: modalFooter
            }, [
              _renderSlot(_ctx.$slots, "footer")
            ], 2))
          : _createCommentVNode("", true)
      ], 512)
    ], 512)
  ], 4))
}
}

})